import { SwapOutlined } from '@ant-design/icons';
import { Card, Col, Divider, Form, Row, Select } from 'antd';
import React, { useMemo } from 'react';
import {
  TRANSFORMATION_TYPES,
  TRANSFORMATIONS_WITH_SOURCE_FIELD,
} from '../../../../../utils/const/mapping';
import { useSelectedField } from '../../../../../utils/zustand';
import Concatenate from './Concatenate';
import SourceFieldSelect from './SourceFieldSelect';
import TransformationForm from './TransformationForm';
import ValuesList from './ValuesList';

const TransformationCard = ({
  transformationData,
  onTransformationChange,
  isControlled = false,
  isDefault = false,
  fieldNamePrefix = [], // Préfixe pour les noms de champs Form.Item
}) => {
  const { selectedField } = useSelectedField();

  // STATE INITIALIZATION
  // If controlled, use props. If not, fallback to zustand (legacy behavior).
  const effectiveType = isControlled
    ? transformationData?.type
    : selectedField?.transformation?.[selectedField?.transformation_type]
        ?.transformation?.type;

  const effectiveParams = isControlled
    ? transformationData?.params
    : selectedField?.transformation?.[selectedField?.transformation_type]
        ?.transformation?.params;

  const effectiveSourceField = isControlled
    ? transformationData?.source_field
    : selectedField?.transformation?.[selectedField?.transformation_type]
        ?.transformation?.source_field;

  // HANDLERS TODO: optim this
  const handleTypeChange = (value) => {
    if (isControlled) {
      onTransformationChange({
        ...transformationData,
        type: value,
        params: {}, // Reset params on type change
        source_field: TRANSFORMATIONS_WITH_SOURCE_FIELD.includes(value)
          ? transformationData?.source_field
          : undefined, // Keep source_field if needed, otherwise reset
      });
    } else {
      // Legacy local state handling would go here if we kept it,
      // but for the plan we focus on the controlled mode.
      console.warn('Uncontrolled mode not fully implemented in refactor');
    }
  };

  const handleParamsChange = (newParams) => {
    if (isControlled) {
      onTransformationChange({
        ...transformationData,
        params: newParams,
      });
    }
  };

  const handleSourceFieldChange = (value) => {
    if (isControlled) {
      onTransformationChange({
        ...transformationData,
        source_field: value,
      });
    }
  };

  // Vérifie si le type de transformation nécessite un champ source
  const needsSourceField =
    TRANSFORMATIONS_WITH_SOURCE_FIELD.includes(effectiveType);

  // Vérifie si l'attribut cible (Mirakl) est de type 'list'
  const isTargetList = useMemo(() => {
    return selectedField?.format?.toLowerCase() === 'list';
  }, [selectedField]);

  // Options du Select de type de transformation
  const transformationTypeOptions = useMemo(() => {
    if (isTargetList) {
      // Si l'attribut cible est une liste, on force "set_value"
      return [{ label: 'Définir une valeur', value: 'set_value' }];
    }
    return TRANSFORMATION_TYPES.map((transformation) => ({
      label: transformation.label,
      value: transformation.value,
    }));
  }, [isTargetList]);

  // Options pour le Select des valeurs Mirakl
  const mpListOptions = useMemo(() => {
    if (!selectedField?.mpListValues) return [];
    return selectedField.mpListValues.map((item) => ({
      value: item.code,
      label: `${item.label} (${item.code})`,
    }));
  }, [selectedField]);

  return (
    <Card
      size="small"
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SwapOutlined style={{ color: isDefault ? '#d075ff' : '#1890ff' }} />
          Transformation
        </span>
      }
      style={{
        marginTop: 8,
        backgroundColor: isDefault ? 'transparent' : '#1890ff14',
        borderColor: isDefault ? '#d075ff' : '#1890ff',
      }}
      styles={{
        body: { padding: '16px' },
      }}
    >
      {/* Type selection row */}
      <Row gutter={16} align="top">
        <Col span={8}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Type de transformation */}
            <Form.Item
              label="Type de transformation"
              name={[...fieldNamePrefix, 'type']}
              rules={[
                {
                  required: true,
                  message: 'Veuillez sélectionner un type de transformation',
                },
              ]}
              initialValue={effectiveType}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                onChange={handleTypeChange}
                placeholder="Sélectionnez un type"
                options={transformationTypeOptions}
              />
            </Form.Item>
            {/* Sélection du champ source */}
            {needsSourceField && (
              <SourceFieldSelect
                value={effectiveSourceField}
                onChange={handleSourceFieldChange}
                transformationType={effectiveType}
                fieldNamePrefix={[...fieldNamePrefix, 'source_field']}
              />
            )}
          </div>
        </Col>
        <Col span={16}>
          {isTargetList && effectiveType === 'set_value' && (
            <Form.Item
              label="Valeur Marketplace"
              name={[...fieldNamePrefix, 'params', 'value']}
              rules={[
                {
                  required: true,
                  message: 'Veuillez sélectionner une valeur',
                },
              ]}
              initialValue={effectiveParams?.value}
            >
              <Select
                style={{ width: '100%' }}
                placeholder="Sélectionnez une valeur Marketplace"
                showSearch
                optionFilterProp="label"
                allowClear
                options={mpListOptions}
                onChange={(value) => handleParamsChange({ value })}
              />
            </Form.Item>
          )}
          {!isTargetList && (
            <TransformationForm
              fieldTransformationType={effectiveType}
              fieldTransformationValue={effectiveParams}
              onChange={handleParamsChange}
              fieldNamePrefix={[...fieldNamePrefix, 'params']}
            />
          )}
        </Col>
      </Row>

      {/* Concatenate elements section - inside the same card */}
      {effectiveType === 'concatenate' && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <Concatenate value={effectiveParams} onChange={handleParamsChange} />
        </>
      )}
      {/* Values list elements section - inside the same card */}
      {effectiveType === 'list' && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <ValuesList value={effectiveParams} onChange={handleParamsChange} />
        </>
      )}
    </Card>
  );
};

export default TransformationCard;
